import api from '../../services/api';

/*
 * Preparing one order on the warehouse floor: picking, parcels, label and
 * dispatch.
 *
 * The endpoints are the same ones the OMS uses for parcels; what differs is the
 * picking, which here takes units out of a concrete location and writes the
 * kardex. The OMS's "prepare" only marks the line and stays as a legacy.
 */

/**
 * The lines to prepare, each with where to take it from, in route order.
 *
 * Every suggestion is a pickable location with available stock of that product,
 * nearest first, and carries its lots in FEFO/FIFO order.
 *
 * @param {number|string} orderId
 */
export const getPicklistRequest = async (orderId) => {
  return await api.get(`inventories/picking/orders/${orderId}/picklist/`);
};

/**
 * Takes units out of a location for a line. Writes a PICK in the kardex and
 * leaves the units on the packing table.
 *
 * @param {number} lineId - The `order_product_id` of the line.
 * @param {{slot_id: number, quantity: number, lot?: string, scanned_code?: string}} payload
 */
export const pickLineRequest = async (lineId, payload) => {
  return await api.post(`inventories/picking/lines/${lineId}/pick/`, payload);
};

/**
 * Puts picked units back on a location, and reactivates the reservation.
 *
 * @param {number} lineId
 * @param {{slot_id: number, quantity: number, lot?: string, reason?: string}} payload
 */
export const unpickLineRequest = async (lineId, payload) => {
  return await api.post(`inventories/picking/lines/${lineId}/unpick/`, payload);
};

/**
 * Reports that the location did not hold what the system said. Picks what was
 * found, if anything, and leaves a discrepancy to count — never an adjustment.
 *
 * @param {number} lineId
 * @param {{slot_id: number, found_quantity: number, reason: string, lot?: string, notes?: string}} payload
 */
export const shortPickLineRequest = async (lineId, payload) => {
  return await api.post(`inventories/picking/lines/${lineId}/short-pick/`, payload);
};

/** The parcels of the order's shipment. Paginated by the server; one page is enough. */
export const getPackagesRequest = async (orderId) => {
  return await api.get(`deliveries/${orderId}/delivery_items/list/`, {
    params: { page_size: 100 },
  });
};

/** One more empty parcel at the end. */
export const createPackageRequest = async (orderId) => {
  return await api.post(`deliveries/${orderId}/delivery_items/create/`, {});
};

/** Removes a parcel; the server renumbers the rest and refuses to remove the last one. */
export const deletePackageRequest = async (packageId) => {
  return await api.delete(`deliveries/delivery_items/${packageId}/delete/`);
};

/**
 * The logistic variables of a parcel: weight and the three dimensions, in
 * whole grams and centimetres. When the last parcel gets complete the server
 * moves the shipment to "packed" and says so with `delivery_status_changed`.
 *
 * @param {number} packageId
 * @param {{weight_grams?: number, height_cm?: number, width_cm?: number, length_cm?: number}} payload
 */
export const updatePackageRequest = async (packageId, payload) => {
  return await api.patch(`deliveries/delivery_items/${packageId}/update_logistics/`, payload);
};

/**
 * The packing supplies that can go into a parcel of this order: the active
 * ones of its warehouse, with what is left of each. No cost or price: that is
 * the holder's business, in Insumos.
 *
 * @param {number|string} orderId
 */
export const getPackingOptionsRequest = async (orderId) => {
  return await api.get(`warehouses/packing/orders/${orderId}/options/`);
};

/**
 * Uses a supply in a parcel — the box, the tape — and takes it out of the
 * supplies ledger with a reference to the order. Answers the parcel with its
 * `supplies`.
 *
 * @param {number} packageId
 * @param {{supply_id: number, quantity: number}} payload
 */
export const addPackageSupplyRequest = async (packageId, payload) => {
  return await api.post(`warehouses/packing/items/${packageId}/supplies/`, payload);
};

/** Takes a supply out of a parcel and puts its units back. Answers the parcel. */
export const removePackageSupplyRequest = async (packageSupplyId) => {
  return await api.delete(`warehouses/packing/supplies/${packageSupplyId}/`);
};

/** Commits available stock to what every line still lacks. Moves nothing. */
export const reserveOrderRequest = async (orderId) => {
  return await api.post(`wms/orders/${orderId}/reserve/`);
};

/**
 * Creates the shipment on the courier and brings back tracking and label.
 *
 * The route is called "dispatch_prepared" for historical reasons; it dispatches
 * nothing. It answers `{status: "dispatching"}` and carries on in a task, so the
 * tracking shows up a few seconds later.
 *
 * @param {number} deliveryId
 */
export const labelDeliveryRequest = async (deliveryId) => {
  return await api.post(`deliveries/${deliveryId}/dispatch_prepared/`);
};

/** The courier took the boxes. The only act that takes units out of the inventory. */
export const dispatchOrderRequest = async (orderId) => {
  return await api.post(`wms/orders/${orderId}/dispatch/`);
};
